import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { getConversations, getConversation, getBots, sendChatMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Search,
  MessageSquare,
  Bot,
  User,
  Clock,
  Send,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ConversationsPage = () => {
  const [conversations, setConversations] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBot, setFilterBot] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [conversationsRes, botsRes] = await Promise.all([
        getConversations(),
        getBots()
      ]);
      setConversations(conversationsRes.data);
      setBots(botsRes.data);
    } catch (error) {
      toast.error('Error al cargar las conversaciones');
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setIsDetailOpen(true);
    
    try {
      const response = await getConversation(conversation.id);
      setConversationDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar la conversación');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversationDetail || sendingMessage) return;

    setSendingMessage(true);
    try {
      const response = await sendChatMessage(
        conversationDetail.bot_id,
        newMessage.trim(),
        conversationDetail.customer_phone
      );
      
      // Add messages to the conversation detail
      setConversationDetail(prev => ({
        ...prev,
        messages: [
          ...prev.messages,
          { role: 'user', content: newMessage.trim(), timestamp: new Date().toISOString() },
          { role: 'assistant', content: response.data.response, timestamp: response.data.timestamp }
        ]
      }));
      
      setNewMessage('');
    } catch (error) {
      toast.error('Error al enviar el mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const getBotName = (botId) => {
    const bot = bots.find(b => b.id === botId);
    return bot?.name || 'Bot desconocido';
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBot = filterBot === 'all' || conv.bot_id === filterBot;
    return matchesSearch && matchesBot;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 bg-muted animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="conversations-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-outfit tracking-tight">Conversaciones</h1>
        <p className="text-muted-foreground mt-1">Historial de chats de tus bots</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-emerald-400 transition-colors" />
          <Input
            data-testid="conversation-search-input"
            placeholder="Buscar por teléfono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-950/50 border-white/5 rounded-xl h-11 text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
          />
        </div>

        <Select value={filterBot} onValueChange={setFilterBot}>
          <SelectTrigger className="w-48" data-testid="filter-bot-select">
            <SelectValue placeholder="Filtrar por bot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los bots</SelectItem>
            {bots.map((bot) => (
              <SelectItem key={bot.id} value={bot.id}>
                {bot.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conversations list */}
      {filteredConversations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <MessageSquare className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="empty-state-title">No hay conversaciones</h3>
          <p className="empty-state-description">
            {searchQuery 
              ? 'No se encontraron conversaciones que coincidan'
              : 'Las conversaciones aparecerán aquí cuando los usuarios interactúen con tus bots'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredConversations.map((conversation, index) => (
            <Card 
              key={conversation.id}
              className={`cursor-pointer card-hover animate-fade-in stagger-${(index % 5) + 1}`}
              onClick={() => openConversation(conversation)}
              data-testid={`conversation-card-${conversation.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">{conversation.customer_phone}</p>
                      <p className="text-sm text-muted-foreground">
                        {getBotName(conversation.bot_id)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      conversation.status === 'active' ? 'status-active' : 'status-completed'
                    }`}>
                      {conversation.status === 'active' ? 'Activa' : 'Finalizada'}
                    </span>
                    <p className="text-xs text-muted-foreground mt-2 flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(conversation.last_message_at), 'dd MMM, HH:mm', { locale: es })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Conversation detail sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-outfit flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Conversación
            </SheetTitle>
          </SheetHeader>
          
          {conversationDetail ? (
            <div className="flex flex-col h-[calc(100vh-8rem)] mt-4">
              {/* Conversation info */}
              <div className="p-3 rounded-lg bg-muted/30 mb-4">
                <p className="text-sm font-medium">{conversationDetail.customer_phone}</p>
                <p className="text-xs text-muted-foreground">{getBotName(conversationDetail.bot_id)}</p>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {conversationDetail.messages?.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-3 h-3 text-white" />
                        </div>
                      )}
                      
                      <div className={`max-w-[80%] p-3 rounded-xl text-sm ${
                        message.role === 'user' 
                          ? 'bg-primary text-primary-foreground rounded-tr-sm' 
                          : 'bg-muted rounded-tl-sm'
                      }`}>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        {message.timestamp && (
                          <p className="text-[10px] opacity-60 mt-1">
                            {format(new Date(message.timestamp), 'HH:mm')}
                          </p>
                        )}
                      </div>

                      {message.role === 'user' && (
                        <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center flex-shrink-0">
                          <User className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Send message */}
              <div className="pt-4 border-t mt-4">
                <div className="flex gap-2">
                  <Input
                    data-testid="conversation-reply-input"
                    placeholder="Escribe un mensaje..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="bg-slate-950/50 border-white/5 rounded-xl text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button
                    data-testid="conversation-send-btn"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ConversationsPage;
